import { z } from "zod";

/**
 * allow | block | ask. A guard that fails resolves to `ask` — the runner
 * enforces that; the schema only fixes the three shapes (ARCHITECTURE §5.4).
 */
export const GuardDecisionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("allow") }).strict(),
  z.object({ kind: z.literal("block"), reason: z.string().min(1) }).strict(),
  // `proposedRule`: a sentence the user could add to their always-allow list if this kind of action is in fact fine.
  z.object({ kind: z.literal("ask"), reason: z.string().min(1), summary: z.string().min(1), proposedRule: z.string().min(1).optional() }).strict(),
]);
export type GuardDecision = z.infer<typeof GuardDecisionSchema>;

/**
 * How the model-backed review runs, as in the prototype's Auto-review: `off`
 * skips it, `shadow` classifies in the background and allows at once (the
 * verdict is only logged), `enforce` waits for the verdict and raises a card
 * on a block. The static policy guard runs in every mode.
 */
export const ReviewModeSchema = z.enum(["off", "shadow", "enforce"]);
export type ReviewMode = z.infer<typeof ReviewModeSchema>;

/**
 * The user's standing instructions to the review, as the prototype's
 * auto-review instructions: an on/off switch and two lists of plain
 * sentences the classifier reads, "always allow" and "always block". They
 * are hints to the classifier, not patterns; the static policy is unaffected.
 */
export const REVIEW_INSTRUCTION_MAX_ENTRIES = 20;
export const REVIEW_INSTRUCTION_MAX_CHARS = 1_000;
export const ReviewInstructionsSchema = z
  .object({
    enabled: z.boolean(),
    allow: z.array(z.string()),
    block: z.array(z.string()),
  })
  .strict();
export type ReviewInstructions = z.infer<typeof ReviewInstructionsSchema>;

/** An approval card's life: raised, answered by the user, or expired unanswered. */
export const ApprovalStatusSchema = z.enum(["pending", "approved", "denied", "expired"]);
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;
